import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccordionSection } from '../../../../design-system';
import { Badge } from '@oneaudi/unified-web-alpha-components';
import {
  Filter,
  FilterSelection,
  FilterFrame,
  BadgeWrapper,
  FilterSectionLabel,
} from '../Filters.style';
import FilterExposedHeader from '../FilterExposedHeader';
import useAnimations from '../../../hooks/Animations/useAnimations';
import { ExposedFiltersTypes } from '../../../constants/initialFilters';

interface FilterSectionProps {
  filterType: string;
  selectedFilterLabels: string;
  openedFiltersList: ExposedFiltersTypes;
  handleExposeFilter: (arg: string) => void;
  children: React.ReactNode;
  testId?: string;
}

const FilterSection: React.FC<FilterSectionProps> = ({
  filterType,
  selectedFilterLabels,
  openedFiltersList,
  handleExposeFilter,
  children,
  testId,
}) => {
  const { t } = useTranslation();
  const isAlpha = !process.env.IS_LEGACY;
  const isOpen = !!openedFiltersList[filterType];
  const { dropdownAnimation, filtersAccordianAnimation } = useAnimations(isOpen);

  const label = t(`promo.filters.filterOptions.${filterType}.title`);

  if (isAlpha) {
    const accordionLabel = (
      <FilterSectionLabel>
        <span>{label}</span>
        {selectedFilterLabels && (
          <BadgeWrapper>
            <Badge semanticShape="positive" labelPosition="trailing" label={selectedFilterLabels} />
          </BadgeWrapper>
        )}
      </FilterSectionLabel>
    );

    return (
      <AccordionSection
        open={isOpen}
        onToggle={() => handleExposeFilter(filterType)}
        label={accordionLabel}
        headingTag="h3"
        data-testid={testId}
      >
        {children}
      </AccordionSection>
    );
  }

  return (
    <Filter data-testid={testId}>
      <FilterExposedHeader
        filterType={filterType}
        selectedFilterLabels={selectedFilterLabels}
        handleExposeFilter={handleExposeFilter}
        openedFiltersList={openedFiltersList}
      />
      {dropdownAnimation(
        (props, item) =>
          item && (
            <FilterSelection style={props}>
              <FilterFrame style={filtersAccordianAnimation}>{children}</FilterFrame>
            </FilterSelection>
          ),
      )}
    </Filter>
  );
};

export default FilterSection;
